import { AxiosResponse } from 'axios'
import axiosInstance from './api'
import { PostAnswerData } from '../models/global.types'
import config from '../config/config'
import { PreferenceType } from '../enums/enums'

export const signIn = async (email: string, password: string, clientId: string): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/oauth/token`, {
  grant_type: 'password',
  client_id: clientId,
  username: email,
  password
})

/**
 * @param {boolean} skipStatusValidation - Manually create the request and skip Axios interceptors
 */
export const checkUserStatus = async (
  email: string,
  skipStatusValidation: boolean = false): Promise<AxiosResponse> => {
  if (skipStatusValidation) {
    // Skip response interceptors
    return await axiosInstance.request({
      url: `/v${config.apiVersion}/auth/status?email=${email}`,
      method: 'get',
      validateStatus: null
    })
  }

  return await axiosInstance.get(`/v${config.apiVersion}/auth/status?email=${email}`)
}

export const beginUserActivation = async (email: string): Promise<AxiosResponse> => {
  const response = await axiosInstance.post(`/v${config.apiVersion}/auth/activation/initiate`, { email })
  return response
}

export const checkUserActivation = async (code: string): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/auth/activation/validate?code=${code}`)

export const completeUserActivation = async (payload: any): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/auth/activation/complete`, payload)

export const confirmUserActivation = async (): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/auth/activation/confirm`)

export const fetchCurrentAttempt = async (): Promise<AxiosResponse> => await axiosInstance.get('/v2/quiz/attempts/current', {})

export const fetchAttemptSessions = async (attemptId: string): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/quiz/attempts/${attemptId}/sessions`, {})

export const fetchQuestions = async (attemptId: string, sessionId: string): Promise<AxiosResponse> => await axiosInstance.get(`/v2/quiz/attempts/${attemptId}/sessions/${sessionId}/questions`)

export const getSessionAnswers = async (attemptId: string, sessionId: string, questionId: string, answers: any): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/quiz/attempts/${attemptId}/sessions/${sessionId}/answers`, {
  params: {
    answers: [{
      question_id: questionId,
      answers
    }]
  }
})

export const postSessionAnswers = async (attemptId: string, sessionId: string, answers: PostAnswerData[], updateAttemptScore?: boolean): Promise<AxiosResponse> => {
  return await axiosInstance.post(
    `/v${config.apiVersion}/quiz/attempts/${attemptId}/sessions/${sessionId}/answers`,
    { answers }, // This is the request body
    {
      params: { updateAttemptScore } // This is the configuration object with query params
    }
  )
}

export const startAttempt = async (): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/quiz/attempts`)

export const getAttempts = async (): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/quiz/attempts`)

export const completeAttempt = async (attemptId: string): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/quiz/attempts/${attemptId}/complete`)

export const fetchModules = async (): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/quiz/modules`)

export const getCurrentSession = async (attemptId: string): Promise<AxiosResponse> => await axiosInstance.get(`/v2/quiz/attempts/${attemptId}/sessions/current`)

export const startNextSession = async (attemptId: string): Promise<AxiosResponse> => await axiosInstance.post(`/v2/quiz/attempts/${attemptId}/sessions/next`)

export const completeSession = async (attemptId: string, sessionId: string): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/quiz/attempts/${attemptId}/sessions/${sessionId}/complete`)

export const getCurrentAttemptAndSession = async (): Promise<AxiosResponse> => await axiosInstance.get('/v2/quiz/attempts/current-attempt-and-session')

export const getUserProfile = async (): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/profile/me`)

export const getAccount = async (): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/accounts/me`)

export const updateUserProfile = async (payload: any): Promise<AxiosResponse> => await axiosInstance.put(`/v${config.apiVersion}/profile/me`, payload)

export const changePassword = async (payload: any): Promise<AxiosResponse> => await axiosInstance.put(`/v${config.apiVersion}/profile/password/change`, payload)

export const signInUser = async (email: string, password: string): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/users/signin-user`, { email, password })

export const createUserPerception = async (moduleId: string, action: string, value: number): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/user-perception/${moduleId}/${action}/${value}`, { moduleId, action, value })

export const getUsers = async (): Promise<AxiosResponse> => await axiosInstance.get(`/v${config.apiVersion}/users`)

export const getUsersWithAttempts = async (active?: boolean, status?: string): Promise<AxiosResponse> => {
  const queryParams = []

  if (active === true) {
    queryParams.push(`active=${active.toString()}`)
  }

  if (typeof status === 'string' && status?.length > 0) {
    queryParams.push(`status=${encodeURIComponent(status)}`)
  }

  const queryParamString = queryParams.length > 0 ? '?' + queryParams.join('&') : ''
  return await axiosInstance.get(`/v${config.apiVersion}/users/with-attempts${queryParamString}`)
}

export const processUserFileUpload = async (file: File): Promise<AxiosResponse> => {
  const formData = new FormData()
  formData.append('students', file)

  return await axiosInstance.post(`/v${config.apiVersion}/users/invites/uploads`, formData)
}

export const getUserPreferences = async (skipStatusValidation: boolean = true): Promise<AxiosResponse> => {
  if (skipStatusValidation) {
    return await axiosInstance.request({
      url: `/v${config.apiVersion}/users/get-user-preferences`,
      method: 'get',
      validateStatus: null
    })
  }

  return await axiosInstance.get(`/v${config.apiVersion}/users/get-user-preferences`)
}

export const setUserPreference = async (
  preferenceType: PreferenceType,
  value: string,
  skipStatusValidation: boolean = true
): Promise<AxiosResponse> => {
  if (skipStatusValidation) {
    return await axiosInstance.request({
      url: `/v${config.apiVersion}/users/set-user-preference`,
      method: 'post',
      data: { preferenceType, value },
      validateStatus: null
    })
  }

  return await axiosInstance.post(
    `/v${config.apiVersion}/users/set-user-preference`,
    {
      preferenceType,
      value
    }
  )
}

// SSO Authentication
export const validateSSOToken = async (oktaToken: string): Promise<AxiosResponse> => await axiosInstance.post(`/v${config.apiVersion}/auth/sso/validate`, { oktaToken })

// Survey
export const getSurvey = async (surveyId: string): Promise<AxiosResponse> =>
  await axiosInstance.get(`/v${config.apiVersion}/survey/${surveyId}`)

export const startSurvey = async (surveyId: string): Promise<AxiosResponse> =>
  await axiosInstance.post(`/v${config.apiVersion}/survey/${surveyId}/start`)

export const submitSurveyAnswer = async (
  surveyId: string,
  questionId: string,
  data: { textResponse?: string; choiceIds?: string[] }
): Promise<AxiosResponse> =>
  await axiosInstance.put(
    `/v${config.apiVersion}/survey/${surveyId}/questions/${questionId}/answer`,
    data
  )

export const completeSurvey = async (surveyId: string): Promise<AxiosResponse> =>
  await axiosInstance.post(`/v${config.apiVersion}/survey/${surveyId}/complete`)

const apis = {
  checkUserStatus,
  beginUserActivation,
  checkUserActivation,
  completeUserActivation,
  confirmUserActivation,
  signIn,
  fetchCurrentAttempt,
  fetchAttemptSessions,
  fetchQuestions,
  getSessionAnswers,
  postSessionAnswers,
  startAttempt,
  getAttempts,
  getCurrentAttemptAndSession,
  completeAttempt,
  fetchModules,
  getCurrentSession,
  startNextSession,
  completeSession,
  getUserProfile,
  updateUserProfile,
  changePassword,
  signInUser,
  getAccount,
  createUserPerception,
  getUsers,
  getUsersWithAttempts,
  getUserPreferences,
  setUserPreference,
  validateSSOToken,
  getSurvey,
  startSurvey,
  submitSurveyAnswer,
  completeSurvey
}

export default apis
